'use client';

import Image from '@/components/Image';
import { IS_DE } from '@/utils/constants';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

export default function FaceShapeClient() {
  const t = useTranslations('guide-tips.face-shape');
  // 面部形状数据
  const faceShapes = [
    {
      type: t('oval'),
      image: '/static-page/guide-tips/oval1.png',
      bannerImage: IS_DE
        ? '/static-page/guide-tips/face/cat-eye-banner.png'
        : '/static-page/guide-tips/face_03.jpg',
      bannerImageM: IS_DE
        ? '/static-page/guide-tips/face/face-shape-guide-1.png'
        : '/static-page/guide-tips/face_03_m.jpg',
      color: '#4766c3',
      description: IS_DE
        ? {}
        : {
            top: t('ovalFacesHaveSlightlyWiderCheekbonesAndAGentlyRoundedJawline'),
            bottom: t('mostFrameStylesSuitThisShapeTheOptionsBelowNaturallyComplementYourFeatures'),
          },
      recommendations: [
        {
          type: t('catEye'),
          image: '/static-page/guide-tips/face01.jpg',
          link: '/goods-list/101',
        },
        {
          type: t('round'),
          image: '/static-page/guide-tips/face02.jpg',
          link: '/goods-list/97',
        },
      ],
    },
    {
      type: t('round'),
      image: '/static-page/guide-tips/round1.png',
      bannerImage: IS_DE
        ? '/static-page/guide-tips/face/square-banner.png'
        : '/static-page/guide-tips/face_07.jpg',
      bannerImageM: IS_DE
        ? '/static-page/guide-tips/face/face-shape-guide-2.png'
        : '/static-page/guide-tips/face_07_m.jpg',
      color: '#6bc4c7',
      description: IS_DE
        ? {}
        : {
            top: t('roundFacesAreSoftWithSimilarWidthAcrossTheForeheadAndCheeks'),
            bottom: t('rectangularOrSquareFramesAddStructureAndHelpDefineYourLook'),
          },
      recommendations: [
        {
          type: t('square'),
          image: IS_DE
            ? '/static-page/guide-tips/face/square-1.png'
            : '/static-page/guide-tips/face03.jpg',
          link: '/goods-list/96',
        },
        {
          type: t('rectangle'),
          image: '/static-page/guide-tips/face04.jpg',
          link: '/goods-list/95',
        },
      ],
    },
    {
      type: t('square'),
      image: '/static-page/guide-tips/square1.png',
      bannerImage: IS_DE
        ? '/static-page/guide-tips/face/pilot-banner-1.png'
        : '/static-page/guide-tips/face_09.jpg',
      bannerImageM: IS_DE
        ? '/static-page/guide-tips/face/face-shape-guide-3.png'
        : '/static-page/guide-tips/face_09_m.jpg',
      color: '#c4af4b',
      description: IS_DE
        ? {}
        : {
            top: t('squareFacesFeatureABroadForeheadStrongCheekbonesAndADefinedJawline'),
            bottom: t('roundOrOvalFramesCanSoftenAnglesAndCreateBalance'),
          },
      recommendations: IS_DE
        ? [
            {
              type: t('oval'),
              image: '/static-page/guide-tips/face06.jpg',
              link: '/goods-list/94',
            },
            {
              type: t('round'),
              image: '/static-page/guide-tips/face/round-2.png',
              link: '/goods-list/97',
            },
          ]
        : [
            {
              type: t('round'),
              image: '/static-page/guide-tips/face05.jpg',
              link: '/goods-list/97',
            },
            {
              type: t('oval'),
              image: '/static-page/guide-tips/face06.jpg',
              link: '/goods-list/94',
            },
          ],
    },
    {
      type: t('heart'),
      image: '/static-page/guide-tips/heart1.png',
      bannerImage: IS_DE
        ? '/static-page/guide-tips/face/pilot-banner-2.png'
        : '/static-page/guide-tips/face_13.jpg',
      bannerImageM: IS_DE
        ? '/static-page/guide-tips/face/face-shape-guide-4.png'
        : '/static-page/guide-tips/face_13_m.jpg',
      color: '#ba6045',
      description: IS_DE
        ? {}
        : {
            top: t('thisShapeHasAWideForeheadHighCheekbonesAndANarrowChin'),
            bottom: t('aviatorOrButterflyFramesHelpBalanceYourProportionsAndAddSoftness'),
          },
      recommendations: [
        {
          type: t('aviator'),
          image: '/static-page/guide-tips/face07.jpg',
          link: '/goods-list/100',
        },
        {
          type: t('butterfly'),
          image: '/static-page/guide-tips/face08.jpg',
          link: '/goods-list/247',
        },
      ],
    },
    {
      type: t('triangle'),
      image: '/static-page/guide-tips/triangle1.png',
      bannerImage: IS_DE
        ? '/static-page/guide-tips/face/browline-banner.png'
        : '/static-page/guide-tips/face_14.jpg',
      bannerImageM: IS_DE
        ? '/static-page/guide-tips/face/face-shape-guide-5.png'
        : '/static-page/guide-tips/face_14_m.jpg',
      color: '#b84782',
      description: IS_DE
        ? {}
        : {
            top: t('triangleFacesHaveAWiderJawAndNarrowerForehead'),
            bottom: t('browlineAndCatEyeFramesDrawAttentionUpwardAndBalanceYourOverallShape'),
          },
      recommendations: [
        {
          type: t('browline'),
          image: IS_DE
            ? '/static-page/guide-tips/face/browline-1.png'
            : '/static-page/guide-tips/face09.jpg',
          link: '/goods-list/99',
        },
        {
          type: t('catEye'),
          image: '/static-page/guide-tips/face10.jpg',
          link: '/goods-list/101',
        },
      ],
    },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div className="w-full">
      {/* PC端：80%宽度，移动端：100%宽度 */}
      <div className="w-full md:w-[80%] mx-auto md:px-[15px] md:px-0">
        {/* 标题区域 */}
        <div className="pt-[36px] md:pt-0">
          <h2 className="text-[31px] md:text-[42px] font-bb pb-[13px] md:mb-[31px] leading-[33px] md:leading-normal">
            {t('eyeglassesForYourFaceShapeTitle')}
          </h2>
          <h3 className="text-[18px] md:text-[28px] font-bb my-[30px] md:my-5 leading-[24px] md:leading-normal text-primary-black">
            {t('findThePerfectEyeglassesForYourFaceShape')}
          </h3>
          <p className="text-[14px] md:text-[16px] leading-[21px] md:leading-[24px] mb-[38px]">
            {t(
              'selectingTheRightEyeglassesIsSimpleWhenYouKnowYourFaceShapeOnceYouIdentifyYourFaceShapeWellRecommendFramesThatWillEnhanceYourNaturalFeaturesAndComplementYourLookCheckOutTheStylesBelowForThePerfectFit',
            )}
          </p>
        </div>

        {/* 面部形状图标区域 */}
        <ul className="flex w-full py-[28px] md:py-0 md:mb-[66px]">
          {faceShapes.map((shape, index) => (
            <li
              key={shape.type}
              className={`flex-1 flex flex-col items-center justify-center h-[70px] md:h-[110px] ${
                index % 2 === 0 ? 'bg-[#eaeffb]' : 'bg-[rgba(234,239,251,0.5)] md:bg-[#f4f7fd]'
              }`}
            >
              <div className="w-[22px] pb-[9px] md:pb-0">
                <Image src={shape.image} alt={shape.type} width={22} height={22} />
              </div>
              <span className="text-[14px] md:mt-[14px]">{shape.type}</span>
            </li>
          ))}
        </ul>

        {/* 面部形状详细内容区域 */}
        <ul className="mb-[60px] md:mb-[178px]">
          {faceShapes.map((shape, index) => (
            <li key={shape.type} className="mb-[50px] md:mb-[96px] last:mb-0">
              {/* 顶部banner区域 */}
              <div className="relative w-full">
                <Image
                  src={isMobile ? shape.bannerImageM : shape.bannerImage}
                  alt={`${shape.type} face shape`}
                  width={1200}
                  height={400}
                  className="w-full"
                />
                <div
                  className={`absolute ${
                    isMobile || index % 2 === 0
                      ? 'left-[26px] md:left-[32px]'
                      : 'right-[26px] md:right-[32px]'
                  } top-[25%] md:top-[30%] w-[60%] md:w-[40%] z-[1]`}
                  style={{ color: shape.color }}
                >
                  <p
                    className={`text-[13px] md:text-[16px] leading-[1.5] mb-[12px] md:mb-[25px] lg:mb-[32px] ${
                      shape.type === 'Square' || shape.type === 'Triangle' ? 'mt-[15px]' : ''
                    }`}
                  >
                    {shape.description.top}
                  </p>
                  <p className="text-[13px] md:text-[16px] leading-[1.5]">
                    {shape.description.bottom}
                  </p>
                </div>
              </div>

              {/* 推荐眼镜区域 */}
              <div className="mt-[20px] md:mt-0">
                <ul className="flex justify-center space-x-[20px] md:space-x-0">
                  {shape.recommendations.map((rec) => (
                    <li key={rec.type} className="flex-1 md:w-1/2">
                      <Link
                        href={rec.link}
                        onClick={() => {
                          // ZELL-64382 记录从面部形状页进入商品列表
                          sessionStorage.setItem('zl_goodsListEnterPageScene', '面部形状页');
                        }}
                      >
                        <div className="w-full md:w-[74%] mx-auto mb-[0] md:mb-[22px]">
                          <Image
                            src={rec.image}
                            alt={`${rec.type} glasses`}
                            width={400}
                            height={300}
                            className="w-full"
                          />
                        </div>
                        <div className="w-full md:w-[65%] mx-auto">
                          <span className="block h-[40px] md:h-[60px] leading-[40px] md:leading-[60px] text-[12px] md:text-[20px] text-center text-white bg-black">
                            {IS_DE ? rec.type : t('exploreGlasses', { type: rec.type })}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
